import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useDataAuth } from '../../hooks/useDataAuth';
import { savedApproval, updateApprovalId } from '../../redux/actions/approval';
import { useNotification } from '../../hooks/useNotification';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { fetchApprovalById, fetchGroups, fetchUsers, getCompanies } from '../../services/approval.service';
import { fetchActionsSelect } from '../../services/common.services';
import { ensureArray } from '../../utils/validateResponseApi';
import { safeParseJSON } from '../../utils/jsonValidator';
import { useCompanyConfiguration } from '../../hooks/useCompanyConfiguration';


const INITIAL_FORM = {
    id_company: '',
    required: false,
    priority: '',
    type_authorizer: '',
    general_condition: '',
    id_authorizer: '',
    id_supplier: [],
};

const INITIAL_ROW = {
    data_field: '',
    condition: '',
    field_value: '',
    condition_text: '',
    connector: ''
};

export const AUTHORIZER_AUTOMATIC = 'automatic';
const VIEW_MODE_SUPPLIER = 'supplier';
const VIEW_MODE_MANAGER = 'manager';

const isScalarId = (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return false;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[') || trimmed.includes(',')) return false;
    }
    return true;
};

const looksLikeSupplierRow = (row) => Boolean(
    row?.business_name || row?.identification || row?.nit || row?.supplier_name
);

const resolveApprovalViewMode = (source, item, location) => {
    const config = item?.config || location?.state?.config || {};
    const explicit = config.view_mode || config.viewMode;
    if (explicit === VIEW_MODE_SUPPLIER || config.hide_suppliers === true || config.hideSuppliers === true) {
        return VIEW_MODE_SUPPLIER;
    }
    if (explicit === VIEW_MODE_MANAGER || config.hide_suppliers === false || config.hideSuppliers === false) {
        return VIEW_MODE_MANAGER;
    }
    if (looksLikeSupplierRow(source) && isScalarId(source?.id_supplier || source?.id)) {
        return VIEW_MODE_SUPPLIER;
    }
    return VIEW_MODE_MANAGER;
};

const resolveContextSupplierId = (source) => {
    if (isScalarId(source?.id_supplier)) return source.id_supplier;
    if (looksLikeSupplierRow(source) && isScalarId(source?.id)) return source.id;
    return null;
};

const resolveRuleId = (source, viewMode) => {
    if (source?.id_approval_rules) return source.id_approval_rules;
    if (viewMode === VIEW_MODE_MANAGER && source?.id) return source.id;
    if (viewMode === VIEW_MODE_SUPPLIER && source?.id && !looksLikeSupplierRow(source)) {
        return source.id;
    }
    return null;
};

const parsePipeIds = (value) => {
    if (value === null || value === undefined || value === '') return [];
    return String(value)
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean);
};

const firstRuleId = (value) => parsePipeIds(value)[0] || null;

const parseBoolean = (value) => (
    value === true || value === 1 || value === '1' || value === 'true'
);

const parseSupplierIds = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter(Boolean);
    }
    if (value === null || value === undefined || value === '') return [];
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                    return parsed.map((item) => String(item).trim()).filter(Boolean);
                }
            } catch {
                return [];
            }
        }
        return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
    }
    return [String(value)];
};

const parseConditionCatalog = (responseData) => {
    const data = ensureArray(responseData);
    if (!data.length) return [];
    if (data[0]?.code && data[0]?.name) return data;
    const rawValue = data[0]?.value;
    if (!rawValue) return [];
    const parsed = typeof rawValue === 'string' ? safeParseJSON(rawValue) : rawValue;
    return Array.isArray(parsed) ? parsed : [];
};

const parseDetail = (detail) => {
    const parsed = typeof detail === 'string' ? safeParseJSON(detail) : detail;
    return ensureArray(parsed).map((row) => ({
        data_field: row.data_field || row.code || '',
        condition: row.condition || '',
        field_value: row.field_value ?? '',
        connector: row.connector || ''
    }));
};

export const buildApprovalPayload = (formData, rows, idApproval = null) => {
    const condition_text = rows.map((row, index, arr) =>
        `${row.condition_text} ${row.field_value} ${index === arr.length - 1 ? '' : ((row.connector === "" ? "" : row.connector === "AND" ? "Y" : "O") || '')}`
    ).join(' ').trim();

    const payload = {
        id_company: formData.id_company,
        required: Boolean(formData.required),
        priority: formData.priority,
        type_authorizer: formData.type_authorizer,
        id_authorizer: formData.type_authorizer === AUTHORIZER_AUTOMATIC ? '' : formData.id_authorizer,
        id_supplier: ensureArray(formData.id_supplier).join(', '),
        general_condition: formData.general_condition,
        condition_text: condition_text === "" ? `Documento que contenga ${formData.general_condition}` : condition_text,
        detail: rows.map((row, index, arr) => ({
            data_field: row.data_field,
            condition: row.condition,
            field_value: row.field_value,
            connector: index === arr.length - 1 ? '' : (row.connector || '')
        }))
    };

    if (idApproval) {
        payload.id_approval_rules = String(idApproval);
    }

    return payload;
};

export const useFormApproval = ({ handleClose, data, item }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [{ userSession }] = useDataAuth();
    const dispatch = useDispatch();
    const [sendNotification] = useNotification();
    const [errorInputs, handleErrorInput] = useErrorInputs({});

    const [formData, setFormData] = useState(INITIAL_FORM);
    const [rowData, setRowData] = useState(INITIAL_ROW);
    const [rows, setRows] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openDialogCompany, setOpenDialogCompany] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [idApproval, setIdApproval] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [authorizers, setAuthorizers] = useState([]);
    const [fields, setFields] = useState([]);

    const {
        activeCompanyId
    } = useCompanyConfiguration();

    const sourceData = useMemo(
        () => data || location?.state?.config?.data || {},
        [data, location?.state?.config?.data]
    );
    const viewMode = useMemo(
        () => resolveApprovalViewMode(sourceData, item, location),
        [sourceData, item, location]
    );
    const isSupplierView = viewMode === VIEW_MODE_SUPPLIER;
    const contextSupplierId = useMemo(
        () => resolveContextSupplierId(sourceData),
        [sourceData]
    );
    const scopedSupplierIds = useMemo(
        () => (contextSupplierId != null ? [contextSupplierId] : []),
        [contextSupplierId]
    );
    const ruleId = resolveRuleId(sourceData, viewMode);
    const selectedCondition = useMemo(
        () => fields.find((field) => String(field.code) === String(rowData.data_field)) || null,
        [fields, rowData.data_field]
    );
    const isAutomatic = formData.type_authorizer === AUTHORIZER_AUTOMATIC;
    const showConnector = editingIndex !== null
        ? editingIndex !== rows.length - 1
        : rows.length > 0;
    const companyInfo = useMemo(
        () => ({ id_company: formData.id_company }),
        [formData.id_company]
    );
    const valueSelectField = useMemo(() => ({
        id: 'field_value',
        label: 'Valor',
        required: true,
        trigger: selectedCondition?.trigger || []
    }), [selectedCondition]);

    const supplierField = useMemo(() => ({
        id: 'id_supplier',
        required: true,
        trigger: [{
            method: 'POST',
            url: process.env.REACT_APP_AWS_API_GATEWAY_URL,
            bodyData: {
                process: 'supplier_select',
                action: 'SELECT_CONCAT',
                variables: JSON.stringify({ id_company: formData.id_company || '' })
            }
        }]
    }), [formData.id_company]);

    const loadAuthorizers = useCallback(async (type) => {
        if (type !== 'group' && type !== 'user') {
            setAuthorizers([]);
            return;
        }
        try {
            if (type === 'group') {
                const response = await fetchGroups(userSession.token, { id_company: activeCompanyId });
                setAuthorizers(ensureArray(response.data).map((group) => ({
                    id: group.id_user_group,
                    name: group.name,
                })));
                return;
            }
            const response = await fetchUsers(userSession.token, { id_company: activeCompanyId });
            setAuthorizers(ensureArray(response.data).map((user) => ({
                id: user.id_user,
                name: user.user_name,
            })));
        } catch (error) {
            console.error("Error fetching authorizers:", error);
            setAuthorizers([]);
        }
    }, [userSession.token]);

    const resetForm = useCallback((keepCompany = false) => {
        setFormData((prev) => ({
            ...INITIAL_FORM,
            id_company: keepCompany ? prev.id_company : (sourceData.id_company || ''),
            id_supplier: isSupplierView ? scopedSupplierIds : []
        }));
        setRowData(INITIAL_ROW);
        setRows([]);
        setEditingIndex(null);
        setAuthorizers([]);
        setIdApproval(null);
        setIsEdit(false);
    }, [isSupplierView, scopedSupplierIds, sourceData.id_company]);

    const fetchCompanies = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getCompanies('38', userSession.token);
            const companyList = ensureArray(response.data);
            setCompanies(companyList);
            if (companyList.length === 1) {
                setFormData((prev) => ({
                    ...prev,
                    id_company: companyList[0].id_company
                }));
                setOpenDialogCompany(false);
            } else {
                setOpenDialogCompany(true);
            }
        } catch (error) {
            console.error("Error fetching companies:", error);
        } finally {
            setLoading(false);
        }
    }, [userSession.token]);

    const fetchIdAppro = useCallback(async () => {
        if (!ruleId) {
            resetForm();
            if (isSupplierView && sourceData.id_company) {
                setFormData((prev) => ({
                    ...prev,
                    id_company: sourceData.id_company,
                    id_supplier: scopedSupplierIds
                }));
                setOpenDialogCompany(false);
                setLoading(false);
                return;
            }
            await fetchCompanies();
            return;
        }

        try {
            setLoading(true);
            setOpenDialogCompany(false);
            const response = await fetchApprovalById(userSession.token, firstRuleId(ruleId) || ruleId);
            if (response.data) {
                const dto = response.data;
                const idsFromRow = sourceData.id_approval_rules || sourceData.id || dto.id_approval_rules;
                setIsEdit(true);
                setIdApproval(parsePipeIds(idsFromRow).join('|') || String(dto.id_approval_rules || ''));
                setFormData({
                    id_company: dto.id_company || sourceData.id_company || '',
                    required: parseBoolean(dto.required),
                    priority: dto.priority ?? '',
                    type_authorizer: dto.type_authorizer || '',
                    id_authorizer: dto.type_authorizer === AUTHORIZER_AUTOMATIC ? '' : (dto.id_authorizer || ''),
                    general_condition: dto.general_condition || '',
                    id_supplier: isSupplierView
                        ? scopedSupplierIds
                        : parseSupplierIds(sourceData.id_supplier || dto.id_supplier),
                });
                setRows(parseDetail(dto.detail));
                if (dto.type_authorizer === 'group' || dto.type_authorizer === 'user') {
                    await loadAuthorizers(dto.type_authorizer);
                }
            } else {
                resetForm();
                if (!(isSupplierView && sourceData.id_company)) {
                    await fetchCompanies();
                }
            }
        } catch (error) {
            console.error("Error fetching approval rule:", error);
            resetForm();
            if (!(isSupplierView && sourceData.id_company)) {
                await fetchCompanies();
            }
        } finally {
            setLoading(false);
        }
    }, [ruleId, userSession.token, fetchCompanies, loadAuthorizers, resetForm, isSupplierView, scopedSupplierIds, sourceData]);

    useEffect(() => {
        fetchIdAppro();
    }, [fetchIdAppro]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchActionsSelect('approval_rule_conditions', userSession.token);
                setFields(parseConditionCatalog(response.data));
            } catch (e) {
                console.error("Error fetching condition catalog:", e.message);
                setFields([]);
            }
        };
        fetchData();
    }, [userSession.token]);

    useEffect(() => {
        if (!fields.length) return;
        setRows((prev) => {
            if (!prev.length) return prev;
            let changed = false;
            const next = prev.map((row) => {
                if (row.condition) return row;
                const match = fields.find((field) => String(field.code) === String(row.data_field));
                if (!match?.condition) return row;
                changed = true;
                return { ...row, condition: match.condition };
            });
            return changed ? next : prev;
        });
    }, [fields, rows.length, idApproval]);

    const validateField = (name, value) => {
        if (Array.isArray(value)) {
            if (!value.length) {
                handleErrorInput(name, { isError: true, messageError: 'Campo requerido' });
                return false;
            }
            handleErrorInput(name, { isError: false, messageError: '' });
            return true;
        }

        const stringValue = typeof value === 'string' ? value : String(value ?? '');
        if (!stringValue.trim()) {
            handleErrorInput(name, { isError: true, messageError: 'Campo requerido' });
            return false;
        }
        handleErrorInput(name, { isError: false, messageError: '' });
        return true;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === 'checkbox' ? checked : value;

        if (name === 'type_authorizer') {
            setFormData((prev) => ({
                ...prev,
                type_authorizer: nextValue,
                id_authorizer: '',
            }));
            loadAuthorizers(nextValue);
            validateField(name, nextValue);
            handleErrorInput('id_authorizer', { isError: false, messageError: '' });
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: nextValue,
        }));
        if (name !== 'required') {
            validateField(name, nextValue);
        }
    };

    const handleSupplierChange = (e) => {
        const value = ensureArray(e.target.value);
        setFormData((prev) => ({
            ...prev,
            id_supplier: value,
        }));
        validateField('id_supplier', value);
    };

    const handleRowChange = (e) => {
        const { name, value } = e.target;
        if (name === 'data_field') {
            const selected = fields.find((field) => String(field.code) === String(value));
            setRowData((prev) => ({
                ...prev,
                data_field: value,
                condition: selected?.condition || '',
                condition_text: selected?.name,
                field_value: '',
            }));
            validateField(name, value);
            handleErrorInput('field_value', { isError: false, messageError: '' });
            return;
        }

        setRowData((prev) => ({
            ...prev,
            [name]: value,
        }));
        validateField(name, value);
    };

    const validateForm = () => {
        let hasError = false;
        const excludedFields = ['id_company', 'required', 'id_supplier', 'general_condition'];
        if (isAutomatic) excludedFields.push('id_authorizer');
        if (isSupplierView) excludedFields.push('id_supplier');

        Object.keys(formData)
            .filter((field) => !excludedFields.includes(field))
            .forEach((field) => {
                if (!validateField(field, formData[field])) hasError = true;
            });

        return !hasError;
    };

    const validateFormRowData = () => {
        let isValid = true;
        const requiredRowFields = ['data_field', 'field_value'];
        if (showConnector) requiredRowFields.push('connector');

        requiredRowFields.forEach((field) => {
            if (!validateField(field, rowData[field])) isValid = false;
        });
        return isValid;
    };

    const handleAddRow = () => {
        if (!validateFormRowData()) return;

        if (editingIndex !== null) {
            setRows((prev) => prev.map((row, index) => (
                index === editingIndex ? { ...rowData } : row
            )));
            setEditingIndex(null);
            sendNotification({ msg: "Condición actualizada.", variant: "success" });
        } else if (rows.length > 0) {
            setRows((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    ...updated[updated.length - 1],
                    connector: rowData.connector
                };
                return [...updated, { ...rowData, connector: '' }];
            });
            sendNotification({ msg: "Condición agregada.", variant: "success" });
        } else {
            setRows([{ ...rowData, connector: '' }]);
            sendNotification({ msg: "Condición agregada.", variant: "success" });
        }

        setRowData(INITIAL_ROW);
        handleErrorInput('data_field', { isError: false, messageError: '' });
        handleErrorInput('field_value', { isError: false, messageError: '' });
        handleErrorInput('connector', { isError: false, messageError: '' });
    };

    const handleEditRow = (index) => {
        setEditingIndex(index);
        setRowData(rows[index]);
    };

    const handleDeleteRow = (index) => {
        setRows((prev) => {
            const updated = prev.filter((_, i) => i !== index);
            if (!updated.length) return updated;
            return updated.map((row, i, arr) => (
                i === arr.length - 1 ? { ...row, connector: '' } : row
            ));
        });
        if (editingIndex === index) {
            setEditingIndex(null);
            setRowData(INITIAL_ROW);
        }
        sendNotification({ msg: "Condición eliminada.", variant: "success" });
    };

    const closeForm = () => {
        setOpenDialogCompany(false);
        if (handleClose) {
            handleClose();
            return;
        }
        navigate(-1);
    };

    const handleCancel = () => {
        closeForm();
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const payload = buildApprovalPayload(formData, rows, isEdit ? idApproval : null);

        try {
            if (isEdit) {
                dispatch(updateApprovalId(userSession.token, JSON.stringify(payload), idApproval));
                sendNotification({
                    msg: "Regla actualizada exitosamente.",
                    variant: "success",
                });
            } else {
                dispatch(savedApproval(userSession.token, JSON.stringify(payload)));
                sendNotification({
                    msg: "Regla guardada exitosamente.",
                    variant: "success",
                });
            }
            closeForm();
        } catch (error) {
            console.error("Error al guardar las reglas:", error);
        }
    };

    const handleConfirmAction = useCallback(async (dto) => {
        setFormData((prev) => ({
            ...prev,
            id_company: dto.id_company,
            id_supplier: isSupplierView ? scopedSupplierIds : []
        }));
        setOpenDialogCompany(false);
    }, [isSupplierView, scopedSupplierIds]);

    const getConditionLabel = (code) => {
        const match = fields.find((field) => String(field.code) === String(code));
        return match?.name || code;
    };

    return {
        formData,
        rowData,
        rows,
        loading,
        openDialogCompany,
        companies,
        authorizers,
        fields,
        errorInputs,
        isSupplierView,
        isAutomatic,
        showConnector,
        selectedCondition,
        companyInfo,
        valueSelectField,
        supplierField,
        handleInputChange,
        handleSupplierChange,
        handleRowChange,
        handleAddRow,
        handleEditRow,
        handleDeleteRow,
        handleCancel,
        handleSave,
        handleConfirmAction,
        getConditionLabel,
    };
};
